import * as op from '../../../common/errors/OperationResult.ts'
import { type OperationResult } from '@/common/errors/OperationResult.ts'
import { AuthStatusService } from './AuthStatusService.ts'
import { localStorageKeys } from '../../configs/local-storage-keys.ts'
import { Observable, Subject } from 'rxjs'
import type { AuthTokensModel } from '@/generated/graphql.ts'
import { StringLocalStorageValue } from '@/common/local-storage/StringLocalStorageValue.ts'
import type { IContainer } from 'bottlejs'
import type { DiId } from '@/common/di/di-ids.ts'
import type { GqlClientService } from '@/common/network/gql-client-service.ts'
import type { IAppLifecycleService } from '@/core/lifecycle/AppLifecycleService.ts'

export class AuthService {
  public onAuthStatusChange$: Observable<boolean>
  private authStatusChangeSubject: Subject<boolean>
  private readonly authToken = new StringLocalStorageValue(localStorageKeys.AuthToken)
  private readonly refreshToken = new StringLocalStorageValue(localStorageKeys.RefreshToken)
  private isAuthed: boolean
  private gqlClient!: GqlClientService
  private refreshInFlight: Promise<boolean> | null = null

  constructor(
    private readonly container: IContainer<DiId>,
    private readonly authStatusService: AuthStatusService,
    private readonly appLifecycleService: IAppLifecycleService,
  ) {
    this.authToken.read()
    this.refreshToken.read()
    this.isAuthed = false
    this.authStatusChangeSubject = new Subject<boolean>()
    this.onAuthStatusChange$ = this.authStatusChangeSubject.asObservable()
    this.authStatusService.onAuthError$.subscribe(this.onAuthError.bind(this))
    this.appLifecycleService.onInit$.subscribe(() => {
      this.gqlClient = this.container['GqlClientService'] as GqlClientService
    })
  }

  public async login(username: string, password: string): Promise<OperationResult<AuthTokensModel>> {
    const query = `mutation($username: String!, $password: String!) {
  login(username: $username, password: $password) {
    token
    refreshToken
  }
}`
    return this.sendAuthMutation('login', query, { username, password })
  }

  public async register(
    username: string,
    password: string,
  ): Promise<OperationResult<AuthTokensModel>> {
    const query = `mutation($username: String!, $password: String!) {
  register(username: $username, password: $password) {
    token
    refreshToken
  }
}`
    return this.sendAuthMutation('register', query, { username, password })
  }

  public async refresh(): Promise<boolean> {
    if (this.refreshInFlight) {
      return this.refreshInFlight
    }
    this.refreshInFlight = (async () => {
      try {
        const refreshed = await this.tryRefresh()
        console.log('auth refresh', { refreshed })
        if (!refreshed) {
          this.logout()
        }
        return refreshed
      } finally {
        this.refreshInFlight = null
      }
    })()
    return this.refreshInFlight
  }

  public notifyAuthSuccess(): void {
    this.emitIfChanged(true)
  }

  public logout(): void {
    localStorage.clear()
    this.authToken.value = undefined
    this.refreshToken.value = undefined
    this.isAuthed = false
    this.authStatusChangeSubject.next(false)
  }

  public isLoggedIn(): boolean {
    return this.authToken.value !== undefined
  }

  public getAuthToken(): string | undefined {
    return this.authToken.value
  }

  private async tryRefresh(): Promise<boolean> {
    const refreshToken = this.refreshToken.value
    if (!refreshToken) {
      return false
    }
    const query = `mutation($refreshToken: String!) {
  refresh(refreshToken: $refreshToken) {
    token
    refreshToken
  }
}`
    const result = await this.sendAuthMutation('refresh', query, { refreshToken })
    return result.isSuccess()
  }

  private emitIfChanged(isAuthed: boolean): void {
    if (this.isAuthed !== isAuthed) {
      this.isAuthed = isAuthed
      this.authStatusChangeSubject.next(isAuthed)
    }
  }

  private onAuthError(): void {
    this.logout()
  }

  private async sendAuthMutation<K extends string>(
    mutationName: K,
    query: string,
    variables: object,
  ): Promise<OperationResult<AuthTokensModel>> {
    const res = await this.gqlClient.sendGqlRequest<Record<K, AuthTokensModel>>(query, {
      variables,
      skipAuth: true,
    })

    if (res.isError()) {
      return op.error(res.error)
    }

    const tokens = res.result?.data?.[mutationName]
    if (!tokens?.token || !tokens?.refreshToken) {
      return op.error('invalid auth response')
    }

    this.authToken.value = tokens.token
    this.refreshToken.value = tokens.refreshToken
    this.emitIfChanged(true)
    return op.success(tokens)
  }
}
